
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Container, Flex, Heading, Select, Stack, Text, RangeSlider, RangeSliderTrack, RangeSliderFilledTrack, RangeSliderThumb, Checkbox, useDisclosure, Button, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Spinner, Alert, AlertIcon, VStack } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { getProductsByCategory } from '../api/products';
import useDeviceType from '../hooks/useDeviceType';
import ProductGrid from '../components/ProductGrid';
import ProductGridSkeleton from '../components/ProductGridSkeleton';
import Sidebar from '../components/Sidebar';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';
import SEO from '../components/SEO';
import ValentinesBanner from '../components/ValentinesBanner';
import { FaFilter } from 'react-icons/fa';

// SEO data for each category
const categoryMeta = {
  rings: {
    title: 'Premium Rings Collection',
    description: 'Explore AGNIR\'s exquisite collection of handcrafted rings. Discover premium rings with fire-inspired aesthetics, from elegant statement pieces to everyday fashion jewelry. Shop unique ring designs crafted with precision.',
    keywords: 'rings, premium rings, fashion rings, handcrafted rings, statement rings, elegant rings, designer rings, gold rings, silver rings, men\'s rings, women\'s rings',
    image: 'https://agnir.co/rings-category.webp'
  },
  bracelets: {
    title: 'Premium Bracelets Collection',
    description: 'Discover AGNIR\'s stunning bracelet collection. Shop handcrafted bracelets with fire-inspired aesthetics, from bold statement pieces to delicate everyday accessories. Premium quality fashion bracelets.',
    keywords: 'bracelets, premium bracelets, fashion bracelets, handcrafted bracelets, statement bracelets, designer bracelets, gold bracelets, silver bracelets, men\'s bracelets, women\'s bracelets',
    image: 'https://agnir.co/bracelets-category.webp'
  },
  chains: {
    title: 'Premium Chains Collection',
    description: 'Browse AGNIR\'s premium chain collection. Explore handcrafted chains with fire-inspired aesthetics, from elegant necklaces to bold statement chains. Shop luxury fashion chains with timeless design.',
    keywords: 'chains, premium chains, fashion chains, handcrafted chains, necklace chains, statement chains, designer chains, gold chains, silver chains, men\'s chains, women\'s chains',
    image: 'https://agnir.co/chains-category.webp'
  },
  pendants: {
    title: 'Premium Pendants Collection',
    description: 'Explore AGNIR\'s elegant pendant collection. Discover handcrafted pendants with fire-inspired aesthetics, unique designs and premium quality. Shop fashion pendants that make a statement.',
    keywords: 'pendants, premium pendants, fashion pendants, handcrafted pendants, statement pendants, designer pendants, gold pendants, silver pendants, necklace pendants, unique pendants',
    image: 'https://agnir.co/pendants-category.webp'
  }
};

const ProductPage = () => {
  const { category } = useParams();
  const deviceType = useDeviceType();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const resetSearchRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getProductsByCategory(category, deviceType);
        const productsWithImageUrls = data.map(product => ({
          ...product,
          image: product.primaryImage ? process.env.REACT_APP_API_BASE_URL + product.primaryImage : null,
        }));
        setProducts(productsWithImageUrls);
        setSearchResults(productsWithImageUrls); // Initialize search results with all products
        
        // Clear search when category changes
        if (resetSearchRef.current) {
          resetSearchRef.current();
        }
      } catch (err) {
        setError('Failed to fetch products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    window.scrollTo(0, 0);
  }, [category, deviceType]);

  const [sort, setSort] = useState('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(12);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'compact'

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleColorChange = (color) => {
    setSelectedColors(prev => 
      prev.includes(color) ? prev.filter(c => c !== color) : [...prev, color]
    );
  };

  const filteredProducts = useMemo(() => {
    if (!searchResults) return [];
    return searchResults
      .filter(p => p.price >= priceRange[0] && p.price <= priceRange[1])
      .filter(p => selectedColors.length === 0 || (p.filters && p.filters.Color && p.filters.Color.some(c => selectedColors.includes(c))));
  }, [searchResults, priceRange, selectedColors]);

  const sortedProducts = useMemo(() => {
    let sorted = [...filteredProducts];
    if (sort === 'price-asc') {
      sorted.sort((a, b) => a.price - b.price);
    } else if (sort === 'price-desc') {
      sorted.sort((a, b) => b.price - a.price);
    } else if (sort === 'name-asc') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'name-desc') {
      sorted.sort((a, b) => b.name.localeCompare(a.name));
    }
    return sorted;
  }, [filteredProducts, sort]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = pageNumber => setCurrentPage(pageNumber);

  const handleSearchResults = useCallback((results) => {
    setSearchResults(results);
    setCurrentPage(1); // Reset to first page when search changes
  }, []);

  // Get category-specific SEO data
  const categoryLower = category?.toLowerCase() || '';
  const seoData = categoryMeta[categoryLower] || {
    title: `${category} Collection`,
    description: `Explore AGNIR's premium ${category} collection. Shop handcrafted jewelry with fire-inspired aesthetics.`,
    keywords: `${category}, premium ${category}, AGNIR ${category}`,
    image: 'https://agnir.co/hero-bg.webp'
  };

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        image={seoData.image}
        url={`https://agnir.co/products/${categoryLower}`}
        breadcrumbs={[
          { name: 'Home', url: 'https://agnir.co/' },
          { name: category, url: `https://agnir.co/products/${categoryLower}` }
        ]}
      />
      <Box bg="gray.50" bgImage={`url(${process.env.REACT_APP_BG_URL})`}>
      <Container maxW={{ base: "container.xl", "2xl": "90%" }} py={{ base: 6, lg: 8 }}>
        <Box 
          bgImage="url('/page-title-bg.jpg')" 
          bgSize="cover" 
          bgPosition="center" 
          py={{ base: 4, md: 6, lg: 5 }} 
          textAlign="center" 
          color="black" 
          mb={{ base: 6, lg: 8 }}
          borderRadius="md"
        >
          <Heading 
            as="h1" 
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            fontWeight="700" 
            textTransform="capitalize" 
            mb={{ base: 2, md: 2, lg: 1 }}
            fontFamily="'Cormorant', serif"
            letterSpacing="0.5px"
          >
            {category}
          </Heading>
          <Text 
            fontSize={{ base: "md", md: "lg" }} 
            lineHeight={{ lg: "1.2" }}
            fontFamily="'Zain', sans-serif"
            fontWeight="500"
          >
            Browse our collection of exquisite {category}.
          </Text>
        </Box>
        
        {/* Valentine's Day Banner */}
        <ValentinesBanner variant="inline" />
        
        {/* Modern Search Section */}
        
          
            <SearchBar
              products={products}
              onSearchResults={handleSearchResults}
              placeholder={`Search ${category.toLowerCase()}...`}
              loading={loading}
              maxSuggestions={6}
              resetSearchRef={resetSearchRef}
            />
          
        
        <Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 8, lg: 10 }}>
          <Box w={{ base: '100%', lg: '22%' }} display={{ base: 'none', lg: 'block' }}>
            <Sidebar 
              priceRange={priceRange}
              setPriceRange={setPriceRange}
            />
          </Box>
          <Box w={{ base: '100%', lg: '78%' }}>
            <Flex 
              direction={{ base: 'column', md: 'row' }} 
              justify="space-between" 
              align="center" 
              mb={8} 
              p={4} 
              bg="white" 
              borderRadius="md" 
              boxShadow="sm"
            >
              <Text 
                mb={{ base: 4, md: 0 }} 
                fontSize={{ base: "sm", md: "md" }}
                fontFamily="'Zain', sans-serif"
                fontWeight="500"
              >
                {`Showing ${indexOfFirstProduct + 1}-${Math.min(
                  indexOfLastProduct,
                  sortedProducts.length
                )} of ${sortedProducts.length} ${sortedProducts.length === 1 ? 'result' : 'results'}`}
                {searchResults.length < products.length && (
                  <Text as="span" color="blue.600" fontWeight="600" ml={2}>
                    (filtered)
                  </Text>
                )}
              </Text>
              <Flex align="center" gap={3} flexWrap="wrap">
                <Button 
                  onClick={onOpen} 
                  display={{ base: 'flex', lg: 'none' }} 
                  leftIcon={<FaFilter />} 
                  bg="black"
                  color="white"
                  fontFamily="'Zain', sans-serif"
                  _hover={{ bg: "gray.800" }}
                  _active={{ bg: "gray.900" }}
                  size="sm"
                >
                  Filters
                </Button>
                
                {/* View Mode Toggle - Mobile Only */}
                <Flex 
                  display={{ base: 'flex', lg: 'none' }}
                  bg="gray.100"
                  borderRadius="md"
                  p={1}
                  gap={1}
                >
                  <Button
                    size="sm"
                    bg={viewMode === 'grid' ? 'black' : 'transparent'}
                    color={viewMode === 'grid' ? 'white' : 'gray.600'}
                    onClick={() => setViewMode('grid')}
                    _hover={{ bg: viewMode === 'grid' ? 'gray.800' : 'gray.200' }}
                    fontFamily="'Zain', sans-serif"
                    fontSize="xs"
                  >
                    1 Col
                  </Button>
                  <Button
                    size="sm"
                    bg={viewMode === 'compact' ? 'black' : 'transparent'}
                    color={viewMode === 'compact' ? 'white' : 'gray.600'}
                    onClick={() => setViewMode('compact')}
                    _hover={{ bg: viewMode === 'compact' ? 'gray.800' : 'gray.200' }}
                    fontFamily="'Zain', sans-serif"
                    fontSize="xs"
                  >
                    2 Col
                  </Button>
                </Flex>
                
                <Select
                  w={{ base: '100%', md: '200px' }}
                  value={sort}
                  onChange={e => setSort(e.target.value)}
                  fontFamily="'Zain', sans-serif"
                  fontWeight="500"
                  size="sm"
                >
                  <option value="relevance">Relevance</option>
                  <option value="name-asc">Name, A to Z</option>
                  <option value="name-desc">Name, Z to A</option>
                  <option value="price-asc">Price, low to high</option>
                  <option value="price-desc">Price, high to low</option>
                </Select>
              </Flex>
            </Flex>
            {loading ? (
              <ProductGrid loading={loading} />
            ) : error ? (
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            ) : currentProducts.length === 0 ? (
              <Box textAlign="center" py={12}>
                <Heading 
                  size="md" 
                  color="gray.600" 
                  mb={4}
                  fontFamily="'Cormorant', serif"
                  fontWeight="700"
                >
                  {searchResults.length === 0 && products.length > 0 
                    ? "No products match your search" 
                    : "No products found in this category"
                  }
                </Heading>
                <Text 
                  color="gray.500" 
                  mb={6}
                  fontFamily="'Zain', sans-serif"
                >
                  {searchResults.length === 0 && products.length > 0 
                    ? "Try adjusting your search terms or filters" 
                    : "Please check back later for new arrivals"
                  }
                </Text>
                {searchResults.length === 0 && products.length > 0 && (
                  <Button 
                    bg="black"
                    color="white"
                    _hover={{ bg: "gray.800" }}
                    _active={{ bg: "gray.900" }}
                    fontFamily="'Zain', sans-serif"
                    onClick={() => {
                      if (resetSearchRef.current) {
                        resetSearchRef.current();
                      }
                      setCurrentPage(1);
                    }}
                  >
                    View All Products
                  </Button>
                )}
              </Box>
            ) : (
              <ProductGrid products={currentProducts} loading={loading} viewMode={viewMode} />
            )}
            <Pagination
              productsPerPage={productsPerPage}
              totalProducts={sortedProducts.length}
              paginate={paginate}
              currentPage={currentPage}
            />
          </Box>
        </Flex>
      </Container>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Filters</DrawerHeader>
          <DrawerBody>
            <Sidebar 
              priceRange={priceRange}
              setPriceRange={setPriceRange}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
    </>
  );
};

export default ProductPage;
